import { useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import * as XLSX from "xlsx";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { numLang } from "@/lib/num";
import { xLabel, xValue, isMoney } from "@/lib/exportI18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Search, Download, Trash2, ArrowDownWideNarrow, ArrowUpNarrowWide, X } from "lucide-react";

const flat = (v) => (v == null ? "" : typeof v === "object" ? Object.values(v).map(flat).join(" ") : String(v));

// Search text, date range, date sort and row selection for any client-side table.
export function useTableTools(rows, { date = (r) => r.date || r.created_at, text } = {}) {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("");
  const [sel, setSel] = useState([]);
  useEffect(() => {
    setSel((s) => { const n = s.filter((id) => rows.some((r) => r.id === id)); return n.length === s.length ? s : n; });
  }, [rows]);
  const view = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const d = (r) => String(date(r) || "").slice(0, 10);
    let out = rows.filter((r) => (!ql || (text ? text(r) : flat(r)).toLowerCase().includes(ql))
      && (!from || d(r) >= from) && (!to || d(r) <= to));
    if (sort) out = [...out].sort((a, b) => (sort === "asc" ? 1 : -1) * d(a).localeCompare(d(b)));
    return out;
  }, [rows, q, from, to, sort, date, text]);
  const allOn = view.length > 0 && view.every((r) => sel.includes(r.id));
  return {
    q, setQ, from, setFrom, to, setTo, sort, setSort, view, sel, setSel, allOn,
    toggle: (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])),
    toggleAll: () => setSel(allOn ? [] : view.map((r) => r.id)),
    selRows: rows.filter((r) => sel.includes(r.id)),
    reset: () => { setQ(""); setFrom(""); setTo(""); setSort(""); },
  };
}

// Excel export in the chosen language; money cells stay numbers with a 2-decimal format.
export function exportRows(rows, cols, filename) {
  const lang = numLang();
  const heads = cols.map(([label]) => xLabel(label, lang));
  const data = rows.map((r) => Object.fromEntries(cols.map(([, get], i) => [heads[i], xValue(typeof get === "function" ? get(r) : r[get], lang)])));
  const ws = XLSX.utils.json_to_sheet(data, { header: heads });
  const money = cols.map(([label]) => isMoney(label));
  for (let r = 1; r <= rows.length; r += 1) {
    money.forEach((m, c) => {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (m && cell && cell.t === "n") cell.z = "#,##0.00";
    });
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function SelectAllBox({ tools, id }) {
  return <Checkbox checked={tools.allOn} onCheckedChange={tools.toggleAll} data-testid={`${id}-select-all`} />;
}

export function SelectBox({ tools, row, id }) {
  return <Checkbox checked={tools.sel.includes(row.id)} onCheckedChange={() => tools.toggle(row.id)} onClick={(e) => e.stopPropagation()} data-testid={`${id}-select-${row.id}`} />;
}

// Toolbar: search, date range + sort, export (selected or all filtered), delete selected, extra buttons (import, add).
export function TableToolbar({ tools, id, cols, filename, onDelete, hideDate, children }) {
  const { lang } = useI18n();
  const n = tools.sel.length;
  const doExport = () => {
    const rows = n ? tools.selRows : tools.view;
    if (!rows.length) { toast.error("—"); return; }
    exportRows(rows, cols, filename || id);
    toast.success(`${fl("tExport", lang)}: ${rows.length}`);
  };
  const doDelete = async () => {
    if (!window.confirm(fl("tConfirmDel", lang).replace("{n}", n))) return;
    const res = await onDelete(tools.sel);
    const fail = (res && res.failed) || 0;
    toast[fail ? "warning" : "success"](`${fl("tDeleted", lang).replace("{n}", n - fail)}${fail ? ` · ${fl("tFailed", lang).replace("{n}", fail)}${res.message ? ` — ${res.message}` : ""}` : ""}`);
    tools.setSel([]);
  };
  const active = tools.q || tools.from || tools.to || tools.sort;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`${id}-toolbar`}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder={fl("tSearch", lang)} value={tools.q} onChange={(e) => tools.setQ(e.target.value)} className="pl-9 h-9 w-44" data-testid={`${id}-search`} />
      </div>
      {!hideDate && <>
        <DateInput value={tools.from} onChange={(e) => tools.setFrom(e.target.value)} className="h-9 w-36" title={fl("tFrom", lang)} data-testid={`${id}-date-from`} />
        <DateInput value={tools.to} onChange={(e) => tools.setTo(e.target.value)} className="h-9 w-36" title={fl("tTo", lang)} data-testid={`${id}-date-to`} />
        <Button variant="outline" size="sm" className="h-9" onClick={() => tools.setSort(tools.sort === "asc" ? "desc" : "asc")} data-testid={`${id}-sort-date`}>
          {tools.sort === "asc" ? <ArrowUpNarrowWide className="h-4 w-4 mr-1" /> : <ArrowDownWideNarrow className="h-4 w-4 mr-1" />}
          {tools.sort === "asc" ? fl("tOldest", lang) : fl("tNewest", lang)}
        </Button>
      </>}
      {active && <Button variant="ghost" size="sm" className="h-9" onClick={tools.reset} data-testid={`${id}-reset`}><X className="h-4 w-4 mr-1" />{fl("tClear", lang)}</Button>}
      {cols && <Button variant="outline" size="sm" className="h-9" onClick={doExport} data-testid={`${id}-export`}><Download className="h-4 w-4 mr-1" />{n ? `${fl("tExportSel", lang)} (${n})` : fl("tExport", lang)}</Button>}
      {onDelete && n > 0 && <Button variant="destructive" size="sm" className="h-9" onClick={doDelete} data-testid={`${id}-delete-selected`}><Trash2 className="h-4 w-4 mr-1" />{fl("tDeleteSel", lang)} ({n})</Button>}
      {children}
    </div>
  );
}

// Runs one DELETE per id; returns {failed, message} for the toolbar toast.
export async function deleteEach(ids, fn) {
  let failed = 0;
  let message = "";
  for (const id of ids) {
    try { await fn(id); } catch (e) { failed += 1; message = e.response?.data?.detail || message; }
  }
  return { failed, message: typeof message === "string" ? message : "" };
}
